// ESIM pricing data loader and helpers
// Source: data/packages.json (eSIMSupra)

import { computeRetailFromWholesale, marginPct, SHOW_PROFIT } from "@/constants/pricing";

/**
 * Countries blocked from sale. Plans covering ONLY these countries are hidden
 * from the site entirely (no destination page, no plan cards, no checkout).
 * Regional plans that INCLUDE a blocked country alongside others are NOT blocked
 * (e.g. an "Asia" plan covering ID+TH+JP stays visible).
 *
 * Add country codes here (uppercase ISO 3166-1 alpha-2) to instantly disable.
 * Remove to re-enable. No rebuild of packages.json needed.
 */
const BLOCKED_COUNTRIES = new Set([
  'ID', // Indonesia — fraudulent card-testing traffic
]);

export type EsimPlan = {
  Type: string;
  Region: string;
  Name: string;
  DataType: string;
  PriceUSD: number;
  VariantPrice: number | null;
  Code: string;
  GBs: number;
  ValidityDays: number;
  Slug: string;
  SeoSlug: string;
  Coverage: string;
  BillingStarts: string;
  PreInstallDays: number;
  Speed: string;
  SupportTopUpType: string;
  BreakoutIP: string;
  Operators?: { operatorName: string; networkType?: string }[];
  OperatorsMap?: Record<string, { operatorName: string; networkType?: string }[]>;
};

let _cache: EsimPlan[] | null = null;
let _loading: Promise<EsimPlan[]> | null = null;

function slugify(text: string): string {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^\w-]+/g, "")
    .replace(/--+/g, "-");
}

function bytesToGB(n: number): number {
  if (!n || n <= 0) return 0;
  return +(n / (1024 * 1024 * 1024)).toFixed(2);
}

export function retailForPlan(p: EsimPlan): number {
  return computeRetailFromWholesale(p.PriceUSD, p.VariantPrice);
}

const _regionNames: Intl.DisplayNames | null = typeof Intl !== "undefined" && "DisplayNames" in Intl
  ? new Intl.DisplayNames(["en"], { type: "region" })
  : null;

function codeToCountryName(code: string): string {
  if (!code) return code;
  try {
    const up = code.toUpperCase();
    const n = _regionNames ? _regionNames.of(up) : null;
    return n || up;
  } catch {
    return code;
  }
}

function inferDataTypeName(dataType?: number): string {
  return dataType === 2 ? "Daily Unlimited" : "Data in Total";
}

function inferBillingStarts(activeType?: number): string {
  return activeType === 2 ? "First Network Connection" : "On Activation";
}

function minorToUSD(n?: number): number {
  const d = Number(process.env.NEXT_PUBLIC_ESIM_PRICE_DIVISOR || 10000);
  if (!n || n <= 0) return 0;
  return +(n / d).toFixed(2);
}

function normalizeRegionLabel(label: string): string {
  const s = (label || "").trim();
  if (!s) return "";
  const lower = s.toLowerCase();
  if (lower.startsWith("asia")) return "Asia";
  if (lower.startsWith("europe")) return "Europe";
  if (lower.startsWith("global") || lower.startsWith("world")) return "Global";
  if (lower.startsWith("africa")) return "Africa";
  if (lower.startsWith("middle east") || lower.startsWith("middle-east")) return "Middle East";
  if (lower.startsWith("north america")) return "North America";
  if (lower.startsWith("south america")) return "South America";
  if (lower.startsWith("latin america")) return "Latin America";
  if (lower.startsWith("oceania") || lower.startsWith("australia")) return "Oceania";
  if (lower.startsWith("europe & uk") || lower.startsWith("europe and uk")) return "Europe";
  return s.replace(/\s{2,}/g, " ").trim();
}

function prettyRegion(name: string, location: string): string {
  const parts = (location || "").split(",").map(s => s.trim()).filter(Boolean);
  if (parts.length === 1) return parts[0];
  let base = String(name || "");
  base = base.replace(/\(.*?\)/g, "");
  base = base.replace(/\s*[-–—:|]\s*.*$/, "");
  base = base.replace(/\s*\d[\s\S]*$/, "");
  base = base.replace(/\s{2,}/g, " ").trim();
  const normalized = normalizeRegionLabel(base);
  return normalized || "Multi-Area";
}

export function loadPlans(): EsimPlan[] {
  return _cache || [];
}

export async function loadPlansAsync(): Promise<EsimPlan[]> {
  if (_cache) return _cache;
  if (_loading) return _loading;
  _loading = (async () => {
    try {
      const list: any[] = (await import("@/data/public-packages.json")).default as any[];
      const plans: EsimPlan[] = list.map((p) => {
        const locations = String(p.location || "").split(",").map((s: string) => s.trim()).filter(Boolean);
        const isSingle = locations.length === 1;
        const coverageNames: string[] = Array.isArray(p.locationNetworkList)
          ? p.locationNetworkList.map((ln: any) => String(ln?.locationName || "").trim()).filter(Boolean)
          : [];
        const opMap: Record<string, { operatorName: string; networkType?: string }[]> = {};
        if (Array.isArray(p.locationNetworkList)) {
          p.locationNetworkList.forEach((ln: any) => {
            const code = String(ln?.locationCode || "").trim();
            const ops = Array.isArray(ln?.operatorList)
              ? ln.operatorList
                .map((o: any) => ({
                  operatorName: String(o?.operatorName || "").trim(),
                  networkType: String(o?.networkType || "").trim() || undefined,
                }))
                .filter((o: any) => o.operatorName)
              : [];
            if (code && ops.length) opMap[code] = ops;
          });
        }
        const defaultOps = isSingle && locations[0] ? (opMap[locations[0]] || []) : [];
        const name = p.name || "";
        const originalSlug = p.slug || "";
        const seoSlug = `${slugify(name)}-${originalSlug.toLowerCase().replace(/_/g, "-")}`;

        return {
          Type: isSingle ? "Single" : "Multi-Area",
          Region: (isSingle && coverageNames[0]) ? coverageNames[0] : prettyRegion(p.name || "", p.location || ""),
          Name: name,
          DataType: inferDataTypeName(p.dataType),
          PriceUSD: minorToUSD(p.retailPrice),
          VariantPrice: p.retailPrice ? minorToUSD(p.retailPrice) : null,
          Code: isSingle ? (locations[0] || "") : (originalSlug?.split("-")[0] || ""),
          GBs: bytesToGB(Number(p.volume || 0)),
          ValidityDays: Number(p.duration || 0),
          Slug: originalSlug,
          SeoSlug: seoSlug,
          Coverage: p.location || "",
          BillingStarts: inferBillingStarts(p.activeType),
          PreInstallDays: Number(p.unusedValidTime || 0),
          Speed: String(p.speed || ""),
          SupportTopUpType: String(p.supportTopUpType ?? ""),
          BreakoutIP: String(p.ipExport ?? ""),
          Operators: defaultOps,
          OperatorsMap: Object.keys(opMap).length ? opMap : undefined,
        } as EsimPlan;
      }).filter(pl => pl.Name && pl.PriceUSD > 0);

      // Remove plans that cover ONLY a blocked country. Regional plans
      // (covering multiple countries) pass through even if one is blocked.
      const filtered = plans.filter(pl => {
        if (!BLOCKED_COUNTRIES.size) return true;
        const codes = parseCoverageList(pl);
        if (codes.length === 0) return true; // no coverage info, keep
        if (codes.length > 1) return true;   // regional/multi, keep
        return !BLOCKED_COUNTRIES.has(codes[0].toUpperCase());
      });

      _cache = filtered;
      return filtered;
    } catch (e) {
      console.error(e);
      return [];
    } finally {
      _loading = null;
    }
  })();
  return _loading;
}

export function getPlanBySlug(plans: EsimPlan[], slug: string): EsimPlan | undefined {
  const normalized = slug.toLowerCase();
  return plans.find(p => p.SeoSlug.toLowerCase() === normalized || p.Slug.toLowerCase() === normalized);
}

export function listCountries(plans: EsimPlan[]): { code: string; name: string }[] {
  const seen = new Map<string, string>();
  plans.forEach(p => {
    if (p.Type === "Single" && p.Code) {
      seen.set(p.Code, p.Region || p.Code);
    }
    parseCoverageList(p).forEach(code => {
      if (!seen.has(code)) seen.set(code, codeToCountryName(code));
    });
  });
  return Array.from(seen.entries())
    .filter(([code]) => !BLOCKED_COUNTRIES.has(code.toUpperCase()))
    .map(([code, name]) => ({ code, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function getCountryPlans(plans: EsimPlan[], code: string): EsimPlan[] {
  const upper = (code || "").toUpperCase();
  return plans
    .filter(p => {
      const cov = parseCoverageList(p);
      const isRegional = cov.length > 1;
      if (isRegional) return false;
      if (p.Type === "Single" && p.Code?.toUpperCase() === upper) return true;
      return cov.length === 1 && cov[0].toUpperCase() === upper;
    })
    .sort((a, b) => a.PriceUSD - b.PriceUSD);
}

export function formatPrice(n: number): string {
  return `$${n.toFixed(2)}`;
}

export { computeRetailFromWholesale, marginPct, SHOW_PROFIT };

export function getRegionalPlans(plans: EsimPlan[]): EsimPlan[] {
  return plans
    .filter(p => {
      const cov = parseCoverageList(p);
      const isMulti = cov.length > 1 || p.Type.toLowerCase().includes("multi");
      return isMulti && p.Region !== "Global";
    })
    .sort((a, b) => a.Region.localeCompare(b.Region) || a.PriceUSD - b.PriceUSD);
}

export function getGlobalPlans(plans: EsimPlan[]): EsimPlan[] {
  return plans
    .filter(p => p.Region === "Global")
    .sort((a, b) => a.PriceUSD - b.PriceUSD);
}

export function parseCoverageList(p: EsimPlan): string[] {
  const raw = (p.Coverage || "").trim();
  if (!raw) return [];
  return raw.replace(/^["']|["']$/g, "").split(",").map(s => s.trim()).filter(Boolean);
}
